import { useMemo } from 'react';
import { useEventLocale } from '../context/LocaleContext';
import type { DateFormat, FormatDateOptions, NullableDateInput } from '../formatters/dateFormatter';
import { formatDateForLanguage } from '../formatters/dateFormatter';

export type { DateFormat, FormatDateOptions, NullableDateInput } from '../formatters/dateFormatter';

/**
 * @deprecated Use `useEventLocaleFormatter()` instead for better type safety and more features.
 *
 * The new hook provides:
 * - Type-safe formatters (formatDate/formatTime/formatDateTime with specific format types)
 * - Built-in date range formatting (formatDateRange)
 * - Language override support
 *
 * @example
 * ```tsx
 * // Old way
 * const formattedDate = useFormattedDate(date, 'MMMM dd, yyyy');
 *
 * // New way (preferred)
 * const { formatDate } = useEventLocaleFormatter();
 * const formattedDate = formatDate(date, 'MMMM dd, yyyy');
 * ```
 */
export const useFormattedDate = (input: NullableDateInput, format: DateFormat, options?: FormatDateOptions): string | undefined => {
  const { language } = useEventLocale();
  return useMemo(() => formatDateForLanguage(language, input, format, options), [input, format, options, language]);
};
